import { cogoPlatformApiUrl, withCogoDevProxyHeaders } from './cogo-platform-config'
import type { StudioCapabilityKey, StudioCapabilityObservation } from './studio-presentation-profile'

type CapabilityResponse =
  | {
      state: 'available'
      observedAt: string
      evidenceRefs: string[]
    }
  | {
      state: 'unavailable'
      reasonCode: string
      message: string
      observedAt: string
      evidenceRefs: string[]
    }

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function isIsoTimestamp(value: unknown): value is string {
  if (!nonEmptyString(value)) return false
  const parsed = new Date(value)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString() === value
}

function isCapabilityResponse(value: unknown): value is CapabilityResponse {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Record<string, unknown>
  if (candidate.state !== 'available' && candidate.state !== 'unavailable') return false
  if (!isIsoTimestamp(candidate.observedAt)) return false
  if (
    !Array.isArray(candidate.evidenceRefs)
    || candidate.evidenceRefs.length === 0
    || !candidate.evidenceRefs.every(nonEmptyString)
  ) return false
  return candidate.state === 'available'
    || (nonEmptyString(candidate.reasonCode) && nonEmptyString(candidate.message))
}

export async function probeStudioPlatform(
  fetchImpl: typeof fetch = fetch,
  observedAt = new Date().toISOString(),
): Promise<StudioCapabilityObservation> {
  try {
    const healthUrl = cogoPlatformApiUrl('/api/v1/health')
    const response = await fetchImpl(healthUrl, {
      method: 'GET',
      headers: withCogoDevProxyHeaders({ Accept: 'application/json' }, new URL(healthUrl).origin),
    })
    if (!response.ok) {
      return {
        state: 'error',
        reasonCode: 'malformed-response',
        failure: { code: 'platform-health-status', httpStatus: response.status },
      }
    }
    const body = await response.json().catch(() => null) as { status?: unknown } | null
    if (body?.status !== 'ok') {
      return {
        state: 'error',
        reasonCode: 'malformed-response',
        failure: { code: 'platform-health-malformed' },
      }
    }
    return {
      state: 'available',
      observedAt,
      evidenceRefs: [`gateway:/api/v1/health:${observedAt}`],
    }
  } catch {
    return {
      state: 'error',
      reasonCode: 'probe-transport-failed',
      failure: { code: 'platform-unreachable' },
    }
  }
}

export function observationFromCapabilityResponse(
  _key: StudioCapabilityKey,
  status: number,
  payload: unknown,
): StudioCapabilityObservation {
  if (status !== 200 || !isCapabilityResponse(payload)) {
    return {
      state: 'error',
      reasonCode: 'malformed-response',
      failure: { code: 'capability-response-rejected', httpStatus: status },
    }
  }
  if (payload.state === 'available') {
    return {
      state: 'available',
      observedAt: payload.observedAt,
      evidenceRefs: [...payload.evidenceRefs],
    }
  }
  return {
    state: 'unavailable',
    reasonCode: payload.reasonCode,
    message: payload.message,
    observedAt: payload.observedAt,
    evidenceRefs: [...payload.evidenceRefs],
  }
}
